import { Link, useSearchParams } from 'react-router-dom';

import { SEO } from '@platform/seo';

import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { buildSeo } from '@/shared/seo/seoBuilder';

import { usePumsemTree } from '../api/use-pumsem.query';
import { PumsemTreeBrowser } from '../components/PumsemTreeBrowser';
import { usePumsemAdminOverlay } from '../hooks/usePumsemAdminOverlay';
import { PUMSEM_ERROR, codeOf, statusOf } from '../lib/pumsemError';
import { EDITION_PARAM, QUERY_PARAM } from '../lib/pumsemNavigation';
import { PumsemMessage } from './PumsemMessage';

/** 트리 404 — "공개된 판이 없다" 와 "그 판이 없다" 를 여기서 가른다. */
const NOT_FOUND = 404;

/**
 * 검색 전 첫 화면 — **이 판에 무엇이 들어 있는지**를 트리로 보여준다.
 *
 * 예전에는 "검색하세요" 안내만 있었다. 그래서 판을 새로 등록하고 `/pumsem` 을
 * 열면 내용이 하나도 안 보였다 — 데이터는 들어와 있는데 화면에 그것을 여는
 * 길이 없었던 것이다. 실무자는 특정 품으로 점프하러 오지만, 처음 오는 사람과
 * "이번 판에 뭐가 들어왔나"를 보려는 사람에게는 훑기가 있어야 한다.
 *
 * 인기 품셈이나 최근 조회 같은 건 여전히 안 채운다 — 아직 그런 지표가 없고,
 * 지어낸 목록은 실무자에게 잘못된 기준을 심는다.
 */
export function PumsemIndexView() {
  const [params] = useSearchParams();
  const hasQuery = (params.get(QUERY_PARAM) ?? '').trim() !== '';
  const edition = params.get(EDITION_PARAM);

  const { viewingHidden } = usePumsemAdminOverlay(edition);
  const {
    data: tree,
    isPending,
    isError,
    error,
    refetch,
  } = usePumsemTree(edition, viewingHidden);

  const seo = buildSeo({
    title: '품셈해설',
    description:
      '건설공사 표준품셈을 품셈명·코드·직종·자재명으로 찾고, 품과 [주] 적용 조건을 개정판별로 확인합니다.',
    path: PATHS.PUMSEM,
  });

  // 검색 중에는 결과 목록이 주인공이다. 트리까지 같이 그리면 어느 쪽을 봐야
  // 하는지 알 수 없다.
  //
  // "왼쪽 목록" 이라고 쓰지 않는다 — 폰에서는 왼쪽이 없다. 목록은 `☰ 결과`
  // 뒤에 있고, `lg:` 미만에서는 아예 `hidden` 이다.
  if (hasQuery) {
    return (
      <>
        <SEO {...seo} />
        <PumsemMessage
          title="결과 목록에서 품셈을 고르세요."
          body="좁은 화면에서는 위 ☰ 결과 를 누르면 목록이 열립니다. 직종 배지를 누르면 그 직종이 들어간 품만 다시 찾습니다."
        />
      </>
    );
  }

  return (
    <>
      <SEO {...seo} />
      <div className="min-w-0 p-5 sm:p-8">
        <h1 className="text-[24px] font-black tracking-tight text-neutral-900 dark:text-neutral-50">
          {tree?.edition.label ?? '표준품셈'}
        </h1>
        <p className="mt-1.5 mb-6 text-[14.5px] text-neutral-500">
          부문을 펼쳐 훑거나, 위에서 품셈명(PE-X 배관)·코드(6-2-4)·직종명(보통인부)
          으로 바로 찾으세요.
        </p>

        {isPending && (
          <div className="space-y-2" aria-hidden>
            <div className="h-10 rounded-xl bg-neutral-100 dark:bg-white/8" />
            <div className="h-10 rounded-xl bg-neutral-100 dark:bg-white/8" />
            <div className="h-10 rounded-xl bg-neutral-100 dark:bg-white/8" />
          </div>
        )}

        {/*
          404 와 장애를 가른다. 예전엔 **모든** 오류를 "공개된 개정판이 아직
          없습니다" 로 덮었다 — 네트워크가 한 번 끊겨도 그 문구가 떴고
          (`retry: false` 라 재시도도 없다), 없는 판을 `?edition=` 으로
          딥링크한 사람에게는 공개판이 멀쩡히 있는데도 없다고 말했다.
        */}
        {isError &&
          (statusOf(error) === NOT_FOUND ? (
            <div className="rounded-xl border border-dashed border-neutral-200 px-4 py-10 text-center dark:border-white/12">
              <p className="text-[14.5px] text-neutral-500">
                {codeOf(error) === PUMSEM_ERROR.NO_PUBLISHED_EDITION
                  ? '아직 공개된 개정판이 없습니다. 관리자가 판을 공개하면 여기에 표시됩니다.'
                  : `${edition ?? ''} 개정판을 찾을 수 없습니다.`}
              </p>
              {edition && codeOf(error) !== PUMSEM_ERROR.NO_PUBLISHED_EDITION && (
                <Link
                  to={PATHS.PUMSEM}
                  className="mt-2 inline-block text-[14px] text-brand-600 underline underline-offset-2 dark:text-brand-300"
                >
                  최신 개정판 보기
                </Link>
              )}
            </div>
          ) : (
            <p
              className="rounded-xl border border-dashed border-danger/40 px-4 py-10 text-center text-[14.5px] text-neutral-500"
              role="alert"
            >
              목록을 불러오지 못했습니다.{' '}
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => void refetch()}
              >
                다시 시도
              </button>
            </p>
          ))}

        {tree && <PumsemTreeBrowser tree={tree} edition={edition} />}
      </div>
    </>
  );
}
